import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider } from "./contexts/AuthContext";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import LoadingSpinner from "./components/common/LoadingSpinner";

// Lazy load wrappers
const AboutWrapper = lazy(() => import("./components/wrappers/AboutWrapper"));
const BusinessWrapper = lazy(
  () => import("./components/wrappers/BusinessWrapper")
);
const CustomerWrapper = lazy(
  () => import("./components/wrappers/CustomerWrapper")
);

// Lazy load pages for better performance
const Home = lazy(() => import("./pages/Home"));

// 회사소개
const GreetingPage = lazy(() => import("./pages/introduce/GreetingPage"));
const ProjectsPage = lazy(() => import("./pages/introduce/ProjectsPage"));
const HistoryPage = lazy(() => import("./pages/introduce/HistoryPage"));
const LocationPage = lazy(() => import("./pages/introduce/LocationPage"));

// 사업분야
const EngineeringPage = lazy(() => import("./pages/business/EngineeringPage"));
const EquipmentPage = lazy(() => import("./pages/business/EquipmentPage"));
const SoftwarePage = lazy(() => import("./pages/business/SoftwarePage"));

// 소프트웨어 서브 페이지
const EngineeringA = lazy(
  () => import("./pages/business/engineering/EngineeringA")
);
const EngineeringB = lazy(
  () => import("./pages/business/engineering/EngineeringB")
);
const EngineeringC = lazy(
  () => import("./pages/business/engineering/EngineeringC")
);

// 고객센터
const News = lazy(() => import("./pages/cs/News"));
const NewsDetail = lazy(() => import("./pages/cs/NewsDetail"));
const FAQ = lazy(() => import("./pages/cs/FAQ"));
const DataRoom = lazy(() => import("./pages/cs/DataRoom"));
const DataRoomDetail = lazy(() => import("./pages/cs/DataRoomDetail"));

// 관리자
const AdminLogin = lazy(() => import("./pages/admin/AdminLoginNew"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const NewsManagement = lazy(() => import("./pages/admin/NewsManagement"));
const ReferenceManagement = lazy(() => import("./pages/admin/ReferenceManagement"));
const QAManagement = lazy(() => import("./pages/admin/QAManagement"));

// 라우트 디버깅 컴포넌트
const RouteDebugger: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    console.log('📍 Current location:', location.pathname + location.search);
  }, [location]);

  return null;
};

const App: React.FC = () => {
  const { ready } = useTranslation();

  console.log('🚀 App component rendering, i18n ready:', ready);

  // i18n이 준비될 때까지 대기
  if (!ready) return <LoadingSpinner />;

  return (
    <AuthProvider>
      <AdminAuthProvider>
        <LanguageProvider>
          <RouteDebugger />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public routes with Layout */}
              <Route element={<Layout />}>
                <Route path="/" element={<Home />} />
                <Route path="/home" element={<Home />} />

                {/* 회사소개 - with tab navigation */}
                <Route path="/introduce" element={<AboutWrapper />}>
                  <Route path="greeting" element={<GreetingPage />} />
                  <Route path="performance" element={<ProjectsPage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="history" element={<HistoryPage />} />
                  <Route path="location" element={<LocationPage />} />
                  <Route index element={<GreetingPage />} />{" "}
                  {/* Default to greeting */}
                </Route>

                {/* 사업분야 - with tab navigation */}
                <Route path="/business" element={<BusinessWrapper />}>
                  <Route path="engineering" element={<EngineeringPage />}>
                    <Route
                      path="industrial-monitoring-design"
                      element={<EngineeringA />}
                    />
                    <Route path="crane-design" element={<EngineeringB />} />
                    <Route
                      path="dam-structure-analysis"
                      element={<EngineeringC />}
                    />
                    <Route index element={<EngineeringPage />} />{" "}
                  </Route>
                  <Route path="equipment" element={<EquipmentPage />} />
                  <Route path="software" element={<SoftwarePage />} />
                </Route>

                {/* 고객센터 - with tab navigation */}
                <Route path="/cs" element={<CustomerWrapper />}>
                  <Route path="news" element={<News />} />
                  <Route path="news/:id" element={<NewsDetail />} />
                  <Route path="faq" element={<FAQ />} />
                  <Route path="data" element={<DataRoom />} />
                  <Route path="data/:id" element={<DataRoomDetail />} />
                  <Route index element={<News />} /> {/* Default to news */}
                </Route>
              </Route>

              {/* Admin routes without layout */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Admin routes with AdminLayout (AdminLayout handles authentication) */}
              <Route element={<AdminLayout />}>
                <Route path="/admin/dashboard" element={<AdminDashboard />} />
                <Route path="/admin/news" element={<NewsManagement />} />
                <Route path="/admin/reference" element={<ReferenceManagement />} />
                <Route path="/admin/qa" element={<QAManagement />} />
                <Route path="/admin" element={<AdminDashboard />} />
              </Route>

              {/* 404 Fallback */}
              <Route path="*" element={
                <div style={{
                  padding: '2rem',
                  textAlign: 'center',
                  minHeight: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <h1>404 - 페이지를 찾을 수 없습니다</h1>
                  <p>요청하신 페이지가 존재하지 않습니다.</p>
                  <button onClick={() => window.location.href = '/'}>홈으로 돌아가기</button>
                </div>
              } />
            </Routes>
          </Suspense>
        </LanguageProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
};

export default App;
